import { GoogleLogin } from '@react-oauth/google'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { AuthSessionContext } from '../../../../context/auth-session-context'
import { useSignInWithGoogle } from '../../../../hook/use-sign-in-with-google'
import { OverlaySpinner } from '../../util/spinner/overlay-spinner'

interface GoogleSignInButtonProps {
  disabled?: boolean
}

// The GSI "large" button only renders inside a cross-origin iframe, so we overlay it
// invisibly on top of our own styled button. The `width` prop makes GSI render a genuinely
// wider iframe (a real resize → a real hit area), so we set it to the container width to get
// full-width tappability. Height, however, is locked at ~40px and can't be enlarged: CSS
// `transform: scale` does NOT reliably grow a cross-origin iframe's touch target on mobile
// (that left only a thin strip tappable — GH-816). So we center the real 40px hit area
// vertically instead of scaling it; the icon/text sit in that band, so taps land on it.
//
// That fixed 40px is also why the GH-908 height scale below can shrink this button freely: the
// hit area does not scale with the box, so the only question is whether it still fits, and the
// 56px floor leaves it 8px of clearance either side.
const GSI_MIN_WIDTH = 200 // GSI ignores widths below ~200px
const GSI_MAX_WIDTH = 400 // GSI caps the rendered button at 400px

export const GoogleSignInButton = ({ disabled }: GoogleSignInButtonProps): React.ReactElement => {
  const { redirectUrl } = useContext(AuthSessionContext)
  const { handleSuccess, handleError, isLoading } = useSignInWithGoogle(redirectUrl)

  const containerRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const update = (): void => setWidth(el.clientWidth)
    update()

    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // Render the GSI button as wide as the container allows, clamped to GSI's supported range.
  const gsiWidth = Math.min(GSI_MAX_WIDTH, Math.max(GSI_MIN_WIDTH, Math.round(width)))

  return (
    <>
      <div
        ref={containerRef}
        className="group relative w-full h-[72px] lg:h-[clamp(56px,8dvh,72px)] overflow-hidden cursor-pointer rounded-[4px]"
        style={{ pointerEvents: disabled || isLoading ? 'none' : 'auto', opacity: disabled || isLoading ? 0.6 : 1 }}
      >
        <div className="absolute inset-0 flex items-center justify-center gap-3 bg-white border border-[#747775] rounded-[4px] pointer-events-none z-[1] font-medium text-[18px] text-primary-gray transition-colors group-hover:bg-gray-50">
          <svg width="32" height="32" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z"
              fill="#4285F4"
            />
            <path
              d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z"
              fill="#34A853"
            />
            <path
              d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z"
              fill="#FBBC05"
            />
            <path
              d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58Z"
              fill="#EA4335"
            />
          </svg>
          Sign in with Google
        </div>
        {width > 0 && (
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-[0.01] z-[2]"
            style={{ width: gsiWidth }}
          >
            <GoogleLogin
              onSuccess={handleSuccess}
              onError={handleError}
              useOneTap={false}
              theme="outline"
              size="large"
              text="signin_with"
              shape="rectangular"
              width={gsiWidth}
            />
          </div>
        )}
      </div>
      {isLoading && <OverlaySpinner />}
    </>
  )
}
